import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import ChangePassword from './components/ChangePassword';
import ResetPassword from './components/ResetPassword';
import AcceptInvitation from './components/AcceptInvitation';
import Dashboard from './pages/Dashboard';
import { ProjectsProvider } from './context/ProjectsContext';
import { APP_NOTIFY_EVENT, notifyInfo } from './utils/notify';
import { APP_CONFIRM_EVENT, resolveConfirm } from './utils/confirmDialog';
import { isPasswordRecoveryUrl } from './utils/passwordRecovery';

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/" replace />;
  const user = getStoredUser();
  if (user?.must_change_password) return <Navigate to="/change-password" replace />;
  return children;
};

const ChangePasswordRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/" replace />;
  const user = getStoredUser();
  if (!user?.must_change_password) return <Navigate to="/dashboard" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  const user = getStoredUser();
  const recovering = isPasswordRecoveryUrl(window.location.pathname, window.location.search);
  if (recovering) return children;
  if (token && user?.must_change_password) return <Navigate to="/change-password" replace />;
  if (token) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  const [notice, setNotice] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const dismissTimerRef = useRef(null);

  useEffect(() => {
    const pushNotice = (next) => {
      setNotice({ ...next, id: Date.now() });
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = setTimeout(() => setNotice(null), next.durationMs || 2600);
    };

    const handleNotify = (event) => {
      const detail = event?.detail || {};
      pushNotice({
        type: detail.type || 'info',
        title: detail.title || '',
        message: String(detail.message || '').trim() || 'Action completed.',
        durationMs: Number(detail.durationMs) || 2600,
      });
    };

    window.addEventListener(APP_NOTIFY_EVENT, handleNotify);

    const handleConfirm = (event) => {
      const detail = event?.detail || {};
      setConfirmState({ id: detail.id, message: detail.message, title: detail.title || 'Confirm' });
    };
    window.addEventListener(APP_CONFIRM_EVENT, handleConfirm);

    const nativeAlert = window.alert;
    window.alert = (message = '') => notifyInfo(message, 'Notice', 2600);

    return () => {
      window.removeEventListener(APP_NOTIFY_EVENT, handleNotify);
      window.removeEventListener(APP_CONFIRM_EVENT, handleConfirm);
      window.alert = nativeAlert;
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, []);

  // GLOBAL_SUBMIT_GUARD: prevent rapid duplicate submit clicks
  useEffect(() => {
    const lockMs = 1500;

    const onClickCapture = (event) => {
      const btn = event.target?.closest?.('button.btn-primary');
      if (!btn) return;

      // allow explicit opt-out
      if (btn.dataset.allowMultiClick === 'true') return;

      const now = Date.now();
      const lockedUntil = Number(btn.dataset.lockedUntil || '0');

      if (lockedUntil > now) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      btn.dataset.lockedUntil = String(now + lockMs);
      // Defer disable so the click completes first - disabled elements don't receive click events
      setTimeout(() => {
        btn.disabled = true;
      }, 0);
      setTimeout(() => {
        btn.disabled = false;
        btn.dataset.lockedUntil = '0';
      }, lockMs);
    };

    document.addEventListener('click', onClickCapture, true);
    return () => document.removeEventListener('click', onClickCapture, true);
  }, []);

  const closeNotice = () => {
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    setNotice(null);
  };

  const isSuccessNotice =
    (notice?.type || '') === 'success' ||
    /(success|created|added|updated|saved|submitted|registered|assigned|completed)/i.test(notice?.message || '');
  const isErrorNotice = (notice?.type || '') === 'error';
  const isWarningNotice = (notice?.type || '') === 'warning';
  const noticeTitle = notice?.title || (isErrorNotice ? 'Error' : isWarningNotice ? 'Warning' : isSuccessNotice ? 'Submitted' : 'Notice');
  const iconBg = isErrorNotice ? '#fee2e2' : isWarningNotice ? '#fef3c7' : isSuccessNotice ? '#dcfce7' : '#EDE9FE';
  const iconColor = isErrorNotice ? '#b91c1c' : isWarningNotice ? '#b45309' : isSuccessNotice ? '#166534' : '#6D28D9';
  const iconText = isErrorNotice ? '!' : isWarningNotice ? '!' : isSuccessNotice ? '✓' : 'i';

  return (
    <BrowserRouter>
      {notice && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(17, 24, 39, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: 24,
          }}
          onClick={closeNotice}
        >
          <div
            role="status"
            aria-live="polite"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(420px, 100%)',
              maxWidth: 420,
              background: '#ffffff',
              borderRadius: 12,
              border: '1px solid #e5e7eb',
              boxShadow: '0 20px 45px rgba(15, 23, 42, 0.22)',
              padding: 24,
            }}
          >
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: iconBg,
                  color: iconColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  fontWeight: 700,
                  flexShrink: 0,
                  marginTop: 2,
                }}
              >
                {iconText}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 15, color: '#111827', fontWeight: 600 }}>
                  {noticeTitle}
                </p>
                <p style={{ margin: '6px 0 0', fontSize: 14, color: '#374151', lineHeight: 1.5 }}>
                  {notice.message}
                </p>
                <button
                  onClick={closeNotice}
                  style={{
                    marginTop: 16,
                    padding: '8px 20px',
                    background: 'var(--color-brand-primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {confirmState && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(17, 24, 39, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            padding: 24,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(420px, 100%)',
              maxWidth: 420,
              background: '#ffffff',
              borderRadius: 12,
              border: '1px solid #e5e7eb',
              boxShadow: '0 20px 45px rgba(15, 23, 42, 0.22)',
              padding: 24,
            }}
          >
            <p id="confirm-title" style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 600, color: '#111827' }}>
              {confirmState.title}
            </p>
            <p style={{ margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.5 }}>
              {confirmState.message}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
              <button
                onClick={() => {
                  resolveConfirm(confirmState.id, false);
                  setConfirmState(null);
                }}
                style={{
                  padding: '8px 18px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  resolveConfirm(confirmState.id, true);
                  setConfirmState(null);
                }}
                style={{
                  padding: '8px 18px',
                  background: 'var(--color-brand-primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      <Routes>
        <Route path="/" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/change-password" element={<ChangePasswordRoute><ChangePassword forced /></ChangePasswordRoute>} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/accept-invitation" element={<PublicRoute><AcceptInvitation /></PublicRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><ProjectsProvider><Dashboard /></ProjectsProvider></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
